import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../app.js";
import { Api } from "../api.js";
import { Country, countryToCountryFilter } from "../model/country.js";
import { Platform } from "../model/platform.js";
import { Keyword, keywordHasAllApps, KeywordRecommendation, getNaturalKeywordIdString, computeExactMatches } from "../model/keyword.js";
import { Filter } from "bad-words";

import "../components/header-component.ts";
import "../components/rating-component.ts";
import "../components/apps-list-component.ts";
import "../components/dashboard-navbar-component.js";
import "../components/dashboard-drawer-component.js";

import { FilterEvent, SearchEvent } from "../components/searchbar-component.js";
import { App } from "../model/app.js";
import { countryFilterToCountry } from "../model/country_filter.js";

@customElement("search-page")
export class SearchPage extends BaseElement {
    @state() term = "";
    @state() country: Country = Country.UnitedStates;
    @state() platform: Platform = Platform.iPhone;

    @state() keyword: Keyword | undefined;
    @state() apps: App[] = [];
    @state() history: Keyword[] = [];

    @state() loading = false; // To track loading state

    async connectedCallback() {
        super.connectedCallback();
        this.loadHistory();
    }

    async loadHistory() {
        const history = await Api.history();
        if (history instanceof Error) {
            console.error("Error fetching history", history.message);
        } else {
            this.history = history.keywords;
        }
    }

    async fetchKeyword(allApps: boolean) {
        if (!this.term.trim()) {
            alert("Please enter a search term.");
            return;
        }
        const filter = new Filter();
        if (filter.isProfane(this.term)) {
            alert("Please enter a valid keyword. Profanity is not allowed.");
            return;
        }
        this.loading = true;
        const keyword = await Api.keyword(this.term, this.country, this.platform, allApps, true);

        if (keyword instanceof Error) {
            alert("Error loading keyword. Please try again.");
        } else {
            this.keyword = keyword;
            const apps: App[] = [];
            for (const rank of keyword.ranks ?? []) {
                if (rank.app) {
                    apps.push(rank.app);
                } else {
                    break;
                }
            }
            this.apps = apps;
        }

        this.loading = false;
        await this.loadHistory();
    }

    async searchTerm(term: string) {
        this.term = term.trim().toLowerCase();
        await this.fetchKeyword(false);
    }

    async handleSearch(searchEvent: SearchEvent) {
        this.term = searchEvent.detail.term.trim().toLowerCase();
        await this.fetchKeyword(false);
        await this.loadHistory();
    }

    async handleFilter(filterEvent: FilterEvent) {
        this.country = countryFilterToCountry(filterEvent.detail.countryFilter) ?? Country.UnitedStates;
        this.platform = filterEvent.detail.platform;
    }

    render() {
        return html`
            <div class="drawer lg:drawer-open">
                <input id="my-drawer" type="checkbox" class="drawer-toggle" />
                <div class="drawer-content">
                    <dashboard-navbar-component></dashboard-navbar-component>

                    <div class="container p-4 md:p-6 lg:p-8 space-y-4">
                        <div class="flex flex-row items-center space-x-2 pb-4">
                            <div class="text-4xl font-bold w-full">Keyword Search</div>

                            ${this.history?.length > 0
                                ? html`
                                      <button
                                          class="btn btn-s"
                                          @click=${async () => {
                                              const terms = this.history?.map((k) => k.term).join(",");
                                              await navigator.clipboard.writeText(terms);
                                              alert("Keywords copied to clipboard!");
                                          }}
                                          ?disabled=${this.loading}
                                      >
                                          Copy
                                      </button>
                                      <button
                                          class="btn btn-s"
                                          @click=${async () => {
                                              if (confirm("Are you sure you want to clear all history?")) {
                                                  const result = await Api.historyDeleteAll();
                                                  if (!(result instanceof Error)) {
                                                      this.history = [];
                                                  }
                                              }
                                          }}
                                          ?disabled=${this.loading}
                                      >
                                          Clear
                                      </button>
                                  `
                                : ""}
                        </div>

                        <div class="flex flex-wrap gap-2">
                            ${this.history
                                ?.filter(
                                    (keyword) =>
                                        keyword.country.toString() === this.country.toString() &&
                                        keyword.platform.toString() === this.platform.toString()
                                )
                                .map(
                                    (keyword) => html`
                                        <div class="relative group">
                                            <button
                                                class="btn btn-sm"
                                                @click=${async () => {
                                                    this.term = keyword.term;
                                                    this.country = keyword.country;
                                                    this.platform = keyword.platform;
                                                    await this.fetchKeyword(false);
                                                }}
                                                ?disabled=${this.loading}
                                            >
                                                ${keyword.term} <span class="text-gray-500">${keyword.popularity} | ${keyword.difficulty}</span>
                                            </button>
                                            <button
                                                class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center 
                                    opacity-0 group-hover:opacity-100 transition-opacity leading-none pb-0.5"
                                                @click=${async (e: Event) => {
                                                    e.stopPropagation();
                                                    const naturalKeywordIdString = getNaturalKeywordIdString(keyword);
                                                    const result = await Api.historyDelete(naturalKeywordIdString);
                                                    if (!(result instanceof Error)) {
                                                        const historyResult = await Api.history();
                                                        if (!(historyResult instanceof Error)) {
                                                            this.history = historyResult.keywords;
                                                        }
                                                    }
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    `
                                )}
                        </div>

                        <searchbar-component
                            .placeholder=${"Search keyword"}
                            .countryFilter=${countryToCountryFilter(this.country)}
                            .platform=${this.platform}
                            ?loading=${this.loading}
                            @search=${this.handleSearch}
                            @filter=${this.handleFilter}
                        ></searchbar-component>

                        ${this.keyword
                            ? html` <h2 class="pt-4">${this.keyword.term}</h2>

                                  <div class="flex flex-row items-center space-x-8">
                                      <div>
                                          <p class="text-lg text-gray-500">Popularity</p>
                                          <p class="text-5xl text-primary font-bold">${this.keyword.popularity}</p>
                                      </div>
                                      <div>
                                          <p class="text-lg text-gray-500">Difficulty</p>
                                          <p class="text-5xl text-primary font-bold">${this.keyword.difficulty}</p>
                                      </div>
                                      <div>
                                          <p class="text-lg text-gray-500">Results</p>
                                          <p class="text-5xl text-primary font-bold">${(this.keyword.ranks ?? []).length}</p>
                                      </div>
                                  </div>

                                  ${this.keyword && (this.keyword.recommendations ?? []).length > 0
                                      ? html`
                                            <h3>Recommendations</h3>
                                            <div class="flex flex-wrap gap-2">
                                                ${(() => {
                                                    const filtered = (this.keyword.recommendations ?? []).filter((k) => k.popularity > 5);
                                                    // Find the maximum popularity in the dataset
                                                    const maxPopularity = Math.max(...filtered.map((k) => k.popularity));
                                                    const minPopularity = Math.min(...filtered.map((k) => k.popularity));

                                                    return filtered.map((recommendedKeyword: KeywordRecommendation) => {
                                                        // Dynamically scale based on the min and max popularity
                                                        const scaledPopularity =
                                                            (recommendedKeyword.popularity - minPopularity) / (maxPopularity - minPopularity || 1); // Handle divide by zero
                                                        return html`
                                                            <div
                                                                class="px-3 py-1 rounded-full text-sm inline-flex items-center cursor-pointer hover:brightness-90 text-primary-content ${scaledPopularity >
                                                                0.8
                                                                    ? "bg-primary"
                                                                    : scaledPopularity > 0.6
                                                                    ? "bg-primary/80"
                                                                    : scaledPopularity > 0.4
                                                                    ? "bg-primary/60"
                                                                    : scaledPopularity > 0.2
                                                                    ? "bg-primary/40"
                                                                    : "bg-primary/20"}"
                                                                @click=${() => this.searchTerm(recommendedKeyword.name)}
                                                            >
                                                                <span class="font-bold">${recommendedKeyword.name}</span>
                                                            </div>
                                                        `;
                                                    });
                                                })()}
                                            </div>
                                        `
                                      : html``}

                                  <div>
                                      <h3 class="mb-1">Ranks</h3>
                                      <p class="text-sm text-gray-500">
                                          ${computeExactMatches(this.keyword)}/10 Exact <span class="bg-yellow-100 px-1 rounded">Matches</span> in
                                          Title or Subtitle
                                      </p>
                                  </div>
                                  <div class="flex flex-col">
                                      <apps-list-component .term=${this.keyword?.term} .apps=${this.apps}></apps-list-component>

                                      ${!keywordHasAllApps(this.keyword)
                                          ? html`
                                                <button
                                                    class="btn w-full md:max-w-32 mx-auto py-2 px-4"
                                                    @click=${() => this.fetchKeyword(true)}
                                                    ?disabled=${this.loading}
                                                >
                                                    ${this.loading ? html`Loading...` : html`Load More`}
                                                </button>
                                            `
                                          : ""}
                                  </div>`
                            : html``}
                    </div>
                </div>
                <div class="drawer-side">
                    <label for="my-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
                    <dashboard-drawer-component></dashboard-drawer-component>
                </div>
            </div>
        `;
    }
}
